import { useEffect, useMemo, useState } from 'react';
import { CalendarDays, Loader2 } from 'lucide-react';
import { useUpdateLeave } from '../../../services/httpServices/queries';
import { HrModal } from './HrModal';
import { DatePicker } from '../../../components/ui/DatePicker';
import type { Leave } from '../../../services/httpServices/hrService';

const LEAVE_TYPES = [
  { value: 'ANNUAL', label: 'Annual' },
  { value: 'SICK', label: 'Sick' },
  { value: 'CASUAL', label: 'Casual' },
  { value: 'MATERNITY', label: 'Maternity' },
  { value: 'PATERNITY', label: 'Paternity' },
  { value: 'UNPAID', label: 'Unpaid' },
  { value: 'COMPENSATORY', label: 'Compensatory' },
];

const LEAVE_STATUSES = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const inputClass =
  'w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary';

/** Inclusive calendar days between two ISO dates (0 when invalid). */
function inclusiveDays(start: string, end: string) {
  if (!start || !end) return 0;
  const s = new Date(`${start}T00:00:00`).getTime();
  const e = new Date(`${end}T00:00:00`).getTime();
  if (isNaN(s) || isNaN(e) || e < s) return 0;
  return Math.round((e - s) / 86_400_000) + 1;
}

interface LeaveEditModalProps {
  open: boolean;
  record: Leave | null;
  onClose: () => void;
}

export function LeaveEditModal({ open, record, onClose }: LeaveEditModalProps) {
  const updateLeave = useUpdateLeave();

  const [type, setType] = useState('ANNUAL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [status, setStatus] = useState('PENDING');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !record) return;
    setType(record.type || 'ANNUAL');
    setStartDate(record.startDate ? String(record.startDate).slice(0, 10) : '');
    setEndDate(record.endDate ? String(record.endDate).slice(0, 10) : '');
    setReason(record.reason || '');
    setStatus(record.status || 'PENDING');
    setError('');
  }, [open, record]);

  const days = useMemo(() => inclusiveDays(startDate, endDate), [startDate, endDate]);
  const rangeInvalid = !!startDate && !!endDate && days === 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!record) return;
    if (rangeInvalid) {
      setError('End date must be on or after the start date');
      return;
    }
    setError('');
    updateLeave.mutate(
      {
        id: record.id,
        data: {
          type,
          startDate,
          endDate,
          days,
          reason: reason.trim() || null,
          status,
        },
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <HrModal
      open={open}
      onClose={onClose}
      title="Edit Leave"
      subtitle={record ? `${days || record.days || 0} day(s)` : undefined}
      icon={CalendarDays}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="leave-edit-form"
            disabled={updateLeave.isPending}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {updateLeave.isPending && <Loader2 size={16} className="animate-spin" />}
            Save Changes
          </button>
        </>
      }
    >
      <form id="leave-edit-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text mb-1">Leave Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)} className={inputClass}>
              {LEAVE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputClass}>
              {LEAVE_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-text mb-1">Start Date</label>
            <DatePicker
              required
              value={startDate}
              onChange={setStartDate}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">End Date</label>
            <DatePicker
              required
              value={endDate}
              onChange={setEndDate}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Days</label>
            <input type="number" value={days} readOnly disabled className={inputClass} />
          </div>
        </div>

        {rangeInvalid && <p className="text-xs text-danger">End date must be on or after the start date.</p>}
        {error && <p className="text-xs text-danger">{error}</p>}

        <div>
          <label className="block text-sm font-medium text-text mb-1">Reason</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Optional reason"
            className={inputClass}
          />
        </div>
      </form>
    </HrModal>
  );
}
